import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, ChevronLeft, Plus, Search } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import BarcodeScanner from '../components/pantry/BarcodeScanner'

// Common items as text-only tiles. The page's purpose is "I'm out of this —
// add it to my list," so quick-tap shortcuts for staples make sense; the
// emoji decorations don't.
const COMMON = [
  'Milk', 'Eggs', 'Bread', 'Cheese',
  'Chicken', 'Bananas', 'Apples', 'Shampoo',
  'Toilet paper', 'Coffee', 'Olive oil', 'Tomatoes',
] as const

export default function QuickAdd() {
  const navigate     = useNavigate()
  const queryClient  = useQueryClient()
  const { user }     = useAuth()
  const [search,      setSearch]      = useState('')
  const [toast,       setToast]       = useState<string | null>(null)
  const [showScanner, setShowScanner] = useState(false)

  useEffect(() => { document.title = 'Quick Add — Pantry' }, [])

  const { data: householdId } = useQuery({
    queryKey: ['householdId', user?.id],
    enabled:  !!user?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', user!.id)
        .single()
      if (error) throw error
      return data.household_id as string
    },
  })

  const { data: pantryItems } = useQuery({
    queryKey: ['pantry-items', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('pantry_items')
        .select('id, name, status_override')
        .eq('household_id', householdId!)
        .eq('is_active', true)
      return data ?? []
    },
  })

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  const matches = search.trim().length > 0
    ? (pantryItems ?? [])
        .filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 5)
    : []

  // The page's semantic is "I noticed I'm out of X" — both branches set
  // status_override='out'. We do NOT insert a synthetic batch here:
  // current_quantity = 0 is exactly correct ("I have zero of this"), and
  // the Phase 1 trigger keeps it that way.
  async function handleAdd(name: string) {
    const existing = pantryItems?.find(
      i => i.name.toLowerCase() === name.toLowerCase()
    )

    if (existing) {
      await supabase
        .from('pantry_items')
        .update({ status_override: 'out' })
        .eq('id', existing.id)
      showToast(`${name} marked as out`)
    } else {
      await supabase.from('pantry_items').insert({
        household_id:       householdId,
        name:               name.charAt(0).toUpperCase() + name.slice(1),
        display_category:   'other',
        freshness_category: 'pantry_staple',
        is_active:          true,
        confirmation_count: 0,
        location:           'pantry',
        track_expiration:   false,
        status_override:    'out',
      })
      showToast(`${name} added`)
    }

    setSearch('')
    queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
  }

  async function handleMarkOut(item: { id: string; name: string }) {
    await supabase
      .from('pantry_items')
      .update({ status_override: 'out' })
      .eq('id', item.id)
    showToast(`${item.name} marked as out`)
    queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
    setSearch('')
  }

  return (
    <main className="min-h-screen bg-cream pb-16">
      <div className="max-w-[440px] mx-auto px-5 flex flex-col">

        {/* Header */}
        <div className="pt-3 pb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1.5 rounded-full text-ink"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium">
              Quick add
            </p>
            <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mt-0.5">
              Mark something <em className="italic">out</em>
            </h1>
          </div>
        </div>

        {/* Search row */}
        <div className="rounded-2xl border border-border-sand bg-paper flex items-center gap-2 px-3 py-1.5 mb-4">
          <Search className="h-4 w-4 text-ink-muted flex-shrink-0" strokeWidth={2} />
          <input
            type="text"
            placeholder="Search or type an item…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && search.trim()) handleAdd(search.trim())
            }}
            autoFocus
            className="flex-1 py-2 font-serif text-[15px] text-ink placeholder:text-ink-muted placeholder:italic bg-transparent outline-none"
          />
          <button
            type="button"
            onClick={() => setShowScanner(true)}
            className="rounded-full p-2 text-ink-muted hover:text-ink transition-colors"
            aria-label="Scan barcode"
          >
            <Camera className="h-4 w-4" strokeWidth={1.7} />
          </button>
        </div>

        {/* Existing item matches */}
        {matches.length > 0 && (
          <div className="rounded-2xl border border-border-sand bg-paper overflow-hidden mb-4">
            {matches.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleMarkOut(item)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-cream-warm/40 ${
                  idx !== matches.length - 1 ? 'border-b border-border-hairline' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[14px] font-medium text-ink leading-tight truncate">
                    {item.name}
                  </p>
                  <p className="font-serif italic text-[11px] text-ink-muted mt-0.5">
                    Already in pantry — tap to mark out
                  </p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.1em] font-medium text-terracotta flex-shrink-0">
                  Mark out
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Add new option */}
        {search.length > 1 && matches.length === 0 && (
          <button
            type="button"
            onClick={() => handleAdd(search)}
            className="w-full flex items-center gap-3 rounded-2xl border border-forest/40 bg-ok-bg/50 px-4 py-3.5 text-left mb-4 active:bg-ok-bg"
          >
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-forest text-forest-ink">
              <Plus className="h-4 w-4" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-[14px] font-medium text-ink leading-tight">
                Add "{search}"
              </p>
              <p className="font-serif italic text-[11px] text-ink-muted mt-0.5">
                New item — marked as out
              </p>
            </div>
          </button>
        )}

        {/* Common items grid */}
        {!search && (
          <>
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-2">
              Common items
            </p>
            <div className="grid grid-cols-2 gap-2">
              {COMMON.map(name => (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleAdd(name)}
                  className="rounded-2xl border border-border-sand bg-paper px-4 py-3 text-center active:bg-cream-warm/40 transition-colors"
                >
                  <span className="font-serif text-[13px] font-medium text-ink leading-tight">
                    {name}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed top-5 left-1/2 -translate-x-1/2 z-50 rounded-full bg-ink text-cream px-5 py-2.5 font-serif text-[13px] shadow-xl whitespace-nowrap">
            {toast}
          </div>
        )}

        {/* Barcode scanner */}
        {showScanner && (
          <BarcodeScanner
            onResult={(name) => {
              setShowScanner(false)
              handleAdd(name)
            }}
            onClose={() => setShowScanner(false)}
          />
        )}

      </div>
    </main>
  )
}
